package ai.jessie.web.theme

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.until
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private const val CURRENT_PAGE = "[aria-current=\"page\"]"

// Top navigation

val TopNavStyle = CssStyle {
    base {
        css(
            "height" to JessieTokens.NAV_HEIGHT,
            "background" to NavVar.value(),
            "display" to "flex",
            "align-items" to "center",
            "gap" to "4px",
            "padding" to "0 28px",
            "color" to NavTextVar.value(),
        )
    }
    // The links have no break opportunity between them; wrap them as items instead of overflowing.
    cssRule(" nav") {
        css("display" to "flex", "flex-wrap" to "wrap", "gap" to "4px", "min-width" to "0")
    }
    until(Breakpoint.LG) {
        css(
            "padding" to "0 16px",
            "flex-wrap" to "wrap",
            "height" to "auto",
            "min-height" to JessieTokens.NAV_HEIGHT,
        )
    }
}

val NavBrandStyle = CssStyle {
    base {
        css(
            "font-family" to JessieTokens.FONT_BRAND,
            "font-style" to "italic",
            "font-size" to "26px",
            "color" to "#fff",
            "margin-right" to "24px",
        )
    }
}

val NavLinkStyle = CssStyle {
    base {
        css(
            "color" to NavTextVar.value(),
            "font-size" to "14px",
            "font-weight" to "500",
            "padding" to "7px 12px",
            "border-radius" to "8px",
        )
    }
    cssRule(":hover") { css("color" to "#fff") }
    cssRule(CURRENT_PAGE) { css("background" to NavActiveVar.value(), "color" to "#fff") }
}

val NavSpacerStyle = CssStyle.base { css("flex-grow" to "1") }

val NavTimerStyle = CssStyle {
    base {
        css(
            "display" to "inline-flex",
            "align-items" to "center",
            "gap" to "10px",
            "height" to "34px",
            "padding" to "0 12px 0 10px",
            "border-radius" to JessieTokens.RADIUS_PILL,
            "background" to NavActiveVar.value(),
            "color" to "#fff",
            "font-size" to "13px",
            "font-weight" to "500",
        )
    }
    cssRule("::before") {
        css(
            "content" to "\"\"",
            "width" to "8px",
            "height" to "8px",
            "border-radius" to "50%",
            "background" to "#3aae78",
        )
    }
}

val NavIconStyle = CssStyle {
    base {
        css(
            "height" to "34px",
            "width" to "34px",
            "padding" to "0",
            "border" to "0",
            "background" to "transparent",
            "color" to NavTextVar.value(),
            "border-radius" to "8px",
            "display" to "inline-flex",
            "align-items" to "center",
            "justify-content" to "center",
            "cursor" to "pointer",
        )
    }
    cssRule(":hover") { css("color" to "#fff", "background" to NavActiveVar.value()) }
}

val AvatarStyle = CssStyle {
    base {
        css(
            "display" to "inline-flex",
            "align-items" to "center",
            "justify-content" to "center",
            "width" to "34px",
            "height" to "34px",
            "border-radius" to "50%",
            "background" to PrimaryTintVar.value(),
            "color" to PrimaryVar.value(),
            "font-size" to "13px",
            "font-weight" to "700",
        )
    }
}

/** Profile avatar beside the name (`18-EinstellungenProfil.png`). */
val ProfileAvatarStyle = AvatarStyle.extendedByBase {
    css("flex" to "none", "width" to "64px", "height" to "64px", "font-size" to "22px")
}

/** Member avatar on the team cards (`20-EinstellungenTeam.png`). */
val MemberAvatarStyle = AvatarStyle.extendedByBase {
    css(
        "flex" to "none",
        "width" to "44px",
        "height" to "44px",
        "background" to HoursTintVar.value(),
        "color" to HoursVar.value(),
        "font-size" to "15px",
    )
}

/** The "?" avatar of a pending invitation. */
val PendingAvatarStyle = MemberAvatarStyle.extendedByBase {
    css("background" to DangerTintVar.value(), "color" to DangerVar.value())
}

// Page and page header

val PageStyle = CssStyle {
    base {
        css(
            "padding" to "${JessieTokens.PAGE_TOP} ${JessieTokens.PAGE_X} 40px",
            "display" to "flex",
            "flex-direction" to "column",
            "gap" to JessieTokens.SECTION_GAP,
            "max-width" to "1440px",
            "margin" to "0 auto",
            "box-sizing" to "border-box",
            "overflow-wrap" to "anywhere",
        )
    }
    until(Breakpoint.LG) {
        css(
            "padding" to "${JessieTokens.PAGE_TOP_NARROW} ${JessieTokens.PAGE_X_NARROW} 40px",
            "gap" to JessieTokens.SECTION_GAP_NARROW,
        )
    }
}

val PageHeaderStyle = CssStyle.base {
    css(
        "display" to "flex",
        "align-items" to "center",
        "justify-content" to "space-between",
        "gap" to "24px",
        "flex-wrap" to "wrap",
    )
}

val PageHeaderTitleStyle = CssStyle {
    base { css("display" to "flex", "align-items" to "center", "gap" to "32px") }
    until(Breakpoint.LG) { css("flex-wrap" to "wrap", "gap" to "16px") }
}

val PageHeaderKickerStyle = CssStyle.base {
    css("font-size" to "13px", "color" to InkMutedVar.value(), "font-weight" to "500")
}

val PageHeaderSubStyle = CssStyle.base { css("font-size" to "15px", "color" to Ink3Var.value()) }

// Actions wrap below each other instead of widening a narrow page.
val PageHeaderActionsStyle = CssStyle.base {
    css("display" to "flex", "flex-wrap" to "wrap", "gap" to "10px")
}

val CrumbStyle = CssStyle {
    base {
        css(
            "display" to "inline-flex",
            "align-items" to "center",
            "gap" to "6px",
            "font-size" to "13px",
            "color" to Ink3Var.value(),
        )
    }
    cssRule(" a") { css("color" to Ink3Var.value()) }
}

val CrumbCurrentStyle = CssStyle.base { css("color" to InkVar.value(), "font-weight" to "500") }

val KpiStyle = CssStyle {
    base {
        css(
            "display" to "flex",
            "flex-direction" to "column",
            "gap" to "2px",
            "padding" to "0 20px",
            "border-left" to "1px solid ${LineVar.value()}",
        )
    }
    until(Breakpoint.LG) { css("border-left" to "0", "padding" to "0") }
}

val KpiValueStyle = CssStyle.base {
    css(
        "font-family" to JessieTokens.FONT_NUM,
        "font-weight" to "500",
        "font-size" to "22px",
        "font-variant-numeric" to "tabular-nums",
    )
}

val KpiUnitStyle = CssStyle.base {
    css(
        "font-family" to JessieTokens.FONT_UI,
        "font-size" to "13px",
        "color" to InkMutedVar.value(),
    )
}

// Columns and side navigation

val ColumnsStyle = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "${JessieTokens.SIDE_NAV_WIDTH} minmax(0, 1fr)",
            "gap" to JessieTokens.SECTION_GAP,
            "align-items" to "start",
        )
    }
    until(Breakpoint.LG) {
        css(
            "grid-template-columns" to "minmax(0, 1fr)",
            "gap" to JessieTokens.SECTION_GAP_NARROW,
        )
    }
}

val SideNavStyle = CssStyle.base {
    css("display" to "flex", "flex-direction" to "column", "gap" to "4px")
}

val SideItemStyle = CssStyle {
    base {
        css(
            "display" to "flex",
            "align-items" to "center",
            "gap" to "10px",
            "height" to "40px",
            "padding" to "0 12px",
            "border-radius" to JessieTokens.RADIUS_CONTROL,
            "color" to Ink2Var.value(),
            "font-size" to "14px",
            "font-weight" to "500",
        )
    }
    cssRule(":hover") {
        css("color" to InkVar.value(), "background" to SurfaceMutedVar.value())
    }
    cssRule(CURRENT_PAGE) {
        css(
            "background" to SurfaceVar.value(),
            "border" to "1px solid ${LineVar.value()}",
            "color" to InkVar.value(),
            "font-weight" to "600",
        )
    }
}

val SideCountStyle = CssStyle.base {
    css("margin-left" to "auto", "font-size" to "12px", "color" to InkPlaceholderVar.value())
}

val SideGroupStyle = CssStyle.base { css("padding" to "14px 12px 6px") }

/** Cards stacked below each other, e.g. the settings sections. */
val CardStackStyle = CssStyle.base {
    css("display" to "flex", "flex-direction" to "column", "gap" to "20px")
}

// Card

val CardStyle = CssStyle.base {
    css(
        "background" to SurfaceVar.value(),
        "border" to "1px solid ${LineVar.value()}",
        "border-radius" to JessieTokens.RADIUS_CARD,
    )
}

val CardBodyStyle = CssStyle.base {
    css(
        "padding" to JessieTokens.CARD_PAD,
        "display" to "flex",
        "flex-direction" to "column",
        "gap" to "16px",
    )
}

val CardHeadStyle = CssStyle {
    base {
        css(
            "display" to "flex",
            "flex-direction" to "column",
            "gap" to "4px",
            "padding" to "20px 24px 16px",
        )
    }
    cssRule(" p") {
        css("margin" to "0", "font-size" to "13px", "color" to InkMutedVar.value())
    }
}

// A card head on one line: title, a figure and a trailing button (`02-Main.png` day card).
val CardHeadRowStyle = CssStyle {
    base {
        css(
            "display" to "flex",
            "flex-wrap" to "wrap",
            "align-items" to "center",
            "gap" to "4px 10px",
            "padding" to "20px 24px 16px",
        )
    }
    cssRule(" > h2") { css("margin" to "0") }
    cssRule(" > button") { css("margin-left" to "auto") }
}

// A card that opens on demand: `<details>` whose `<summary>` is the head.
val DisclosureStyle = CssStyle {
    cssRule(" > summary") {
        css(
            "padding" to "16px 24px",
            "cursor" to "pointer",
            "font-size" to "15px",
            "font-weight" to "600",
            "border-radius" to JessieTokens.RADIUS_CARD,
        )
    }
    cssRule(" > summary:focus-visible") {
        css("outline" to "2px solid ${PrimaryVar.value()}", "outline-offset" to "-2px")
    }
    cssRule("[open] > summary") {
        css(
            "border-bottom" to "1px solid ${LineVar.value()}",
            "border-radius" to "${JessieTokens.RADIUS_CARD} ${JessieTokens.RADIUS_CARD} 0 0",
        )
    }
}

val CardFootStyle = CssStyle.base {
    css(
        "display" to "flex",
        "flex-wrap" to "wrap",
        "justify-content" to "flex-end",
        "gap" to "8px",
        "padding" to "14px 24px",
        "border-top" to "1px solid ${LineVar.value()}",
        "background" to SurfaceMutedVar.value(),
        "border-radius" to "0 0 ${JessieTokens.RADIUS_CARD} ${JessieTokens.RADIUS_CARD}",
    )
}

/** Running text in a card: paragraphs lose their browser margins; long labels wrap in buttons. */
val TextCardStyle = CssStyle {
    cssRule(" p") { css("margin" to "0") }
    cssRule(" legend") { css("padding" to "0", "font-size" to "15px", "font-weight" to "600") }
    // Organisation names of any length end up in button labels.
    cssRule(" button") {
        css(
            "white-space" to "normal",
            "height" to "auto",
            "min-height" to JessieTokens.CONTROL_HEIGHT,
            "padding-top" to "8px",
            "padding-bottom" to "8px",
            "text-align" to "left",
        )
    }
}

/** The single centred card of the entry frame. */
val EntryCardStyle = CssStyle.base {
    css(
        "width" to "100%",
        "max-width" to "560px",
        "margin" to "0 auto",
        "box-sizing" to "border-box",
    )
}

/** Help and logout below the entry card's content, stacked. */
val EntryFootStyle = CardFootStyle.extendedByBase {
    css("flex-direction" to "column", "align-items" to "flex-start")
}

/** One entry of the top navigation. */
data class NavItem(val label: String, val href: String)

/** One entry of the left sub-navigation; [count] is shown right-aligned. */
data class SideItem(
    val label: String,
    val href: String,
    val icon: String,
    val iconStyle: IconStyle = IconStyle.REGULAR,
    val count: String? = null,
)

/** A captioned group of side-navigation entries; [label] is the group caption. */
data class SideGroup(val label: String, val items: List<SideItem>)

/** A KPI cell for the page header: label above a tabular number. */
data class Kpi(val label: String, val value: String, val unit: String? = null)

/**
 * Dark top bar: word-mark, navigation, optional running-timer chip, colour-mode switch and the
 * avatar linking to the profile. [current] marks the active entry by href.
 */
@Composable
fun topNav(
    items: List<NavItem>,
    current: String?,
    initials: String?,
    profileHref: String = "/profile",
    timer: String? = null,
) {
    Header(attrs = TopNavStyle.toAttrs()) {
        A(href = "/", attrs = NavBrandStyle.toAttrs()) { Text("jessie.ai") }
        if (items.isNotEmpty())
            Nav(attrs = { attr("aria-label", "Hauptnavigation") }) {
                items.forEach { item ->
                    A(
                        href = item.href,
                        attrs =
                            NavLinkStyle.toAttrs {
                                if (item.href == current) attr("aria-current", "page")
                            },
                    ) {
                        Text(item.label)
                    }
                }
            }
        Div(attrs = NavSpacerStyle.toAttrs())
        if (timer != null) Span(attrs = NavTimerStyle.toAttrs()) { Text(timer) }
        colorModeToggle()
        if (initials != null)
            A(href = profileHref, attrs = AvatarStyle.toAttrs { attr("aria-label", "Profil") }) {
                Text(initials)
            }
    }
}

/** Page body below the top bar; the caller renders sections inside. */
@Composable
fun page(lang: String? = null, content: @Composable () -> Unit) {
    Main(attrs = PageStyle.toAttrs { lang?.let { attr("lang", it) } }) { content() }
}

/** Title block with optional kicker, subtitle, title badge, KPI strip and actions on the right. */
@Composable
fun pageHeader(
    title: String,
    kicker: String? = null,
    subtitle: String? = null,
    kpis: List<Kpi> = emptyList(),
    titleId: String = "page-title",
    crumb: (@Composable () -> Unit)? = null,
    badge: (@Composable () -> Unit)? = null,
    actions: (@Composable () -> Unit)? = null,
) {
    Div(attrs = PageHeaderStyle.toAttrs()) {
        Div(attrs = PageHeaderTitleStyle.toAttrs()) {
            Div(attrs = StackStyle.toAttrs()) {
                crumb?.invoke()
                kicker?.let { Span(attrs = PageHeaderKickerStyle.toAttrs()) { Text(it) } }
                // A state badge sits beside the title (`06-Rechnung.png`).
                if (badge == null) pageTitle(title, titleId)
                else
                    Div(attrs = RowStyle.toAttrs()) {
                        pageTitle(title, titleId)
                        badge()
                    }
                subtitle?.let { Span(attrs = PageHeaderSubStyle.toAttrs()) { Text(it) } }
            }
            kpis.forEach { kpi ->
                Div(attrs = KpiStyle.toAttrs()) {
                    Span(attrs = LabelStyle.toAttrs()) { Text(kpi.label) }
                    Span(attrs = KpiValueStyle.toAttrs()) {
                        Text(kpi.value)
                        kpi.unit?.let { Span(attrs = KpiUnitStyle.toAttrs()) { Text(" $it") } }
                    }
                }
            }
        }
        if (actions != null) Div(attrs = PageHeaderActionsStyle.toAttrs()) { actions() }
    }
}

@Composable
private fun pageTitle(title: String, titleId: String) {
    H1(
        attrs = {
            id(titleId)
            attr("tabindex", "-1")
        }
    ) {
        Text(title)
    }
}

/** Breadcrumb line above a detail title: the parent page as a link, then the current name. */
@Composable
fun crumb(parentLabel: String, parentHref: String, current: String) {
    Nav(attrs = CrumbStyle.toAttrs { attr("aria-label", "Pfad") }) {
        A(href = parentHref) { Text(parentLabel) }
        icon("chevron-right", size = 9)
        Span(attrs = CrumbCurrentStyle.toAttrs { attr("aria-current", "page") }) { Text(current) }
    }
}

/** Two-column layout: left sub-navigation, right content. */
@Composable
fun columns(side: @Composable () -> Unit, content: @Composable () -> Unit) {
    Div(attrs = ColumnsStyle.toAttrs()) {
        side()
        Div(attrs = StackStyle.toAttrs()) { content() }
    }
}

/** Left sub-navigation; [current] marks the active entry by href. */
@Composable
fun sideNav(items: List<SideItem>, current: String?, label: String = "Bereich") {
    Nav(attrs = SideNavStyle.toAttrs { attr("aria-label", label) }) {
        items.forEach { sideItem(it, current) }
    }
}

/** Left sub-navigation in captioned groups (settings: Übergreifend / Organisation / …). */
@Composable
fun sideNav(groups: List<SideGroup>, current: String?, label: String) {
    Nav(attrs = SideNavStyle.toAttrs { attr("aria-label", label) }) {
        groups.forEach { group ->
            Span(attrs = listOf(SideGroupStyle, LabelStyle).toAttrs()) { Text(group.label) }
            group.items.forEach { sideItem(it, current) }
        }
    }
}

@Composable
private fun sideItem(item: SideItem, current: String?) {
    A(
        href = item.href,
        attrs = SideItemStyle.toAttrs { if (item.href == current) attr("aria-current", "page") },
    ) {
        icon(item.icon, item.iconStyle)
        Text(item.label)
        item.count?.let { Span(attrs = SideCountStyle.toAttrs()) { Text(it) } }
    }
}

/** A collapsed card; [summary] opens the body. The content stays in the DOM while closed. */
@Composable
fun disclosure(summary: String, content: @Composable () -> Unit) {
    TagElement<HTMLElement>("details", listOf(CardStyle, DisclosureStyle).toAttrs()) {
        TagElement<HTMLElement>("summary", null) { Text(summary) }
        Div(attrs = CardBodyStyle.toAttrs()) { content() }
    }
}

/** A card; [title] and [description] render the settings-style head when given. */
@Composable
fun card(
    title: String? = null,
    description: String? = null,
    footer: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    Div(attrs = CardStyle.toAttrs()) {
        if (title != null) {
            Div(attrs = CardHeadStyle.toAttrs()) {
                H2 { Text(title) }
                description?.let { P { Text(it) } }
            }
        }
        Div(attrs = CardBodyStyle.toAttrs()) { content() }
        if (footer != null) Div(attrs = CardFootStyle.toAttrs()) { footer() }
    }
}
